import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  HeartPulse,
} from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import YearMonthPicker from '../../../components/yearMonthPicker';
import {
  ComplianceDashboardView,
  DeductionCapBreachView,
  ProbationWarningView,
  ComplianceEventView,
  MonthlyPensionContributionView,
  EmiratizationSummaryView,
  SifPreflightView,
} from '../../../models/payroll';
import {
  getComplianceDashboard,
  getDeductionBreaches,
  getProbationWarnings,
  getComplianceHistory,
  resolveComplianceEvent,
} from '../../../api/Payroll/compliance';
import { getSifPreflight } from '../../../api/Payroll/wps';
import {
  getContributions,
  getEmiratizationSummary,
} from '../../../api/Payroll/gpssa';
import { OverviewTab } from './tabs/OverviewTab';
import { ComplianceIssuesTab } from './tabs/ComplianceIssuesTab';
import { EventLogTab } from './tabs/EventLogTab';
import { KsaCchiPanel } from './components/KsaCchiPanel';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';

type TabId = 'overview' | 'cchi' | 'compliance-issues' | 'events';

const ComplianceCenter: React.FC = () => {
  const { t } = useTranslation();
  const country = useComplianceCountry();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dashboard, setDashboard] = useState<ComplianceDashboardView | null>(null);
  const [breaches, setBreaches] = useState<DeductionCapBreachView[]>([]);
  const [probationWarnings, setProbationWarnings] = useState<ProbationWarningView[]>([]);
  const [history, setHistory] = useState<ComplianceEventView[]>([]);

  const [contributions, setContributions] = useState<MonthlyPensionContributionView[]>([]);
  const [emiratization, setEmiratization] = useState<EmiratizationSummaryView | null>(null);
  const [preflight, setPreflight] = useState<SifPreflightView | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Settle independently: some endpoints are UAE-only and 404 for KSA companies.
    // A rejection on one must not blank the others (e.g. KSA probation warnings).
    const settled = await Promise.allSettled([
      getComplianceDashboard(year, month),
      getDeductionBreaches(year, month),
      getProbationWarnings(),
      getComplianceHistory(),
      getContributions(year, month),
      getEmiratizationSummary(),
      getSifPreflight(year, month),
    ]);
    const [dashRes, breachRes, probRes, histRes, contribRes, emiratizationRes, preflightRes] = settled;

    if (dashRes.status === 'fulfilled' && dashRes.value.errors.length === 0) setDashboard(dashRes.value.payload);
    if (breachRes.status === 'fulfilled' && breachRes.value.errors.length === 0) setBreaches(breachRes.value.payload);
    if (probRes.status === 'fulfilled' && probRes.value.errors.length === 0) setProbationWarnings(probRes.value.payload);
    if (histRes.status === 'fulfilled' && histRes.value.errors.length === 0) setHistory(histRes.value.payload);
    if (contribRes.status === 'fulfilled' && contribRes.value.errors.length === 0) setContributions(contribRes.value.payload);
    if (emiratizationRes.status === 'fulfilled' && emiratizationRes.value.errors.length === 0) setEmiratization(emiratizationRes.value.payload);
    if (preflightRes.status === 'fulfilled' && preflightRes.value.errors.length === 0) setPreflight(preflightRes.value.payload);

    // Surface an error banner only on a genuine outage — every call failed (rejected or
    // returned errors). The per-country 404s on UAE-only endpoints never trip this for KSA,
    // because the probation call still resolves successfully.
    const allFailed = settled.every(r => r.status === 'rejected' || r.value.errors.length > 0);
    if (allFailed) {
      const rejected = settled.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
      setError(rejected?.reason instanceof Error ? rejected.reason.message : t('common.error'));
    }
    setLoading(false);
  }, [year, month, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResolve = async (eventId: number) => {
    try {
      const result = await resolveComplianceEvent(eventId);
      if (result.errors.length === 0) await fetchData();
      else setError(result.errors[0].message);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  };

  // A null dashboard means no data — count zero issues (not the "1 + 1" the UAE
  // WPS/GPSSA ternaries would otherwise yield). WPS/GPSSA only count for UAE.
  const issueCount = !dashboard ? 0 :
    dashboard.deductionCapBreachCount +
    dashboard.probationWarningCount +
    (dashboard.basicSalaryInvariantHealthy === false ? 1 : 0) +
    (country === 'UAE'
      ? (dashboard.wpsStatus && !dashboard.wpsStatus.isSubmitted ? 1 : 0) +
        (dashboard.gpssaFullyCovered ? 0 : 1)
      : 0);

  const tabs: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }>; badge?: number }[] = [
    {
      id: 'overview',
      label: t('payroll.complianceCenter.tabs.overview', 'Overview'),
      icon: ShieldCheck,
      badge: issueCount > 0 ? issueCount : undefined,
    },
    // CCHI is a KSA-only concept.
    ...(country === 'KSA'
      ? [{ id: 'cchi' as TabId, label: t('payroll.complianceCenter.tabs.cchi', 'CCHI'), icon: HeartPulse }]
      : []),
    // Compliance Issues (deduction cap + probation) is UAE-shaped; KSA surfaces probation in the Overview.
    ...(country === 'UAE'
      ? [{ id: 'compliance-issues' as TabId, label: t('payroll.complianceCenter.tabs.complianceIssues', 'Compliance Issues'), icon: AlertTriangle }]
      : []),
    { id: 'events', label: t('payroll.complianceCenter.tabs.events', 'Event Log'), icon: TrendingUp },
  ];

  return (
    <PageLayout
      title={t('payroll.complianceCenter.title', 'Compliance Center')}
      icon={ShieldCheck}
      accentColor="rose"
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <YearMonthPicker
            date={new Date(year, month - 1, 1)}
            onChange={d => {
              setYear(d.getFullYear());
              setMonth(d.getMonth() + 1);
            }}
          />
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="flex border-b border-border overflow-x-auto" data-testid="compliance-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge !== undefined && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingState title={t('common.loading')} size="md" />
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverviewTab
                dashboard={dashboard}
                emiratization={emiratization}
                contributions={contributions}
                probationWarnings={probationWarnings}
                preflight={preflight}
                onJumpToTab={setActiveTab}
              />
            )}
            {activeTab === 'cchi' && (
              <KsaCchiPanel onError={setError} />
            )}
            {activeTab === 'compliance-issues' && (
              <ComplianceIssuesTab
                breaches={breaches}
                probationWarnings={probationWarnings}
                emiratization={emiratization}
                emiratizationLoading={loading && emiratization === null}
              />
            )}
            {activeTab === 'events' && (
              <EventLogTab history={history} onResolve={handleResolve} />
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default ComplianceCenter;
